'use client';

import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  emitTrackPropertyMapEntryEvent,
  emitTrackPropertyMapViewEvent,
  emitTrackPropertyMapInteractionEvent,
  type PropertyMapInteraction,
  type PropertyMapSurface,
} from '@/src/analytics/hotpads/events/propertyMap';
import type { HotpadsClickstreamListing } from '@/src/analytics/hotpads/types';

type Impression = 'entry' | 'map';
type PropertyMapAnalytics = {
  impression: (surface: PropertyMapSurface, kind: Impression) => void;
  interaction: (surface: PropertyMapSurface, source: PropertyMapInteraction) => void;
};

const AnalyticsContext = createContext<PropertyMapAnalytics | null>(null);

export function PropertyMapAnalyticsProvider({
  listing,
  children,
}: {
  listing: HotpadsClickstreamListing;
  children: ReactNode;
}) {
  const listingId = `${listing.aliasEncoded}:${listing.maloneLotIdEncoded}`;
  const [visit, setVisit] = useState(() => ({ listingId, seen: new Set<string>() }));
  if (visit.listingId !== listingId) setVisit({ listingId, seen: new Set<string>() });
  const value = useMemo<PropertyMapAnalytics>(
    () => ({
      impression(surface, kind) {
        const key = `${surface}:${kind}`;
        if (visit.seen.has(key)) return;
        visit.seen.add(key);
        const emit = kind === 'entry' ? emitTrackPropertyMapEntryEvent : emitTrackPropertyMapViewEvent;
        emit({ listing, surface });
      },
      interaction(surface, source) {
        emitTrackPropertyMapInteractionEvent({ listing, surface, source });
      },
    }),
    [listing, visit],
  );
  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}

function isForeground(node: Element) {
  return document.visibilityState === 'visible' && !node.closest('[hidden], [inert], [aria-hidden="true"]');
}

function useVisibleImpression(enabled: boolean, threshold: number, onImpression: () => void) {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const visible = useRef(false);
  useEffect(() => {
    visible.current = false;
    if (!enabled || !node || typeof IntersectionObserver === 'undefined') return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let ratio = 0;
    const cancel = () => {
      clearTimeout(timer);
      timer = undefined;
    };
    const update = () => {
      if (ratio < threshold || !isForeground(node)) {
        cancel();
        return;
      }
      if (timer === undefined)
        timer = setTimeout(() => {
          if (isForeground(node)) onImpression();
        }, 1000);
    };
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          ratio = entry.isIntersecting ? entry.intersectionRatio : 0;
          visible.current = ratio > 0;
          update();
        }
      },
      { threshold: [0, threshold] },
    );
    observer.observe(node);
    const visibilityObserver = new MutationObserver(update);
    for (let ancestor: Element | null = node; ancestor; ancestor = ancestor.parentElement) {
      visibilityObserver.observe(ancestor, { attributes: true, attributeFilter: ['hidden', 'inert', 'aria-hidden'] });
    }
    document.addEventListener('visibilitychange', update);
    return () => {
      cancel();
      observer.disconnect();
      visibilityObserver.disconnect();
      document.removeEventListener('visibilitychange', update);
      visible.current = false;
    };
  }, [enabled, node, onImpression, threshold]);

  const recordInteraction = useCallback(() => {
    if (enabled && node && visible.current && isForeground(node)) onImpression();
  }, [enabled, node, onImpression]);
  return { ref: setNode, recordInteraction };
}

export function usePropertyMapTracking(surface: PropertyMapSurface, ready = false) {
  const analytics = useContext(AnalyticsContext);
  const entryImpression = useCallback(() => analytics?.impression(surface, 'entry'), [analytics, surface]);
  const mapImpression = useCallback(() => analytics?.impression(surface, 'map'), [analytics, surface]);
  const { ref: entryRef } = useVisibleImpression(Boolean(analytics), 0.5, entryImpression);
  const { ref: mapRef, recordInteraction } = useVisibleImpression(Boolean(analytics) && ready, 0.25, mapImpression);
  const trackInteraction = useCallback(
    (source: PropertyMapInteraction) => {
      if (document.visibilityState !== 'visible') return;
      recordInteraction();
      analytics?.interaction(surface, source);
    },
    [analytics, recordInteraction, surface],
  );
  return { entryRef, mapRef, trackInteraction };
}
